import { inject } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { JoinSiteExperimentConsumerProps, withJoinSiteExperiment } from '../../experiments/join-site';
import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import Store from '../../store';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import ProductStore, { Product } from '../../store/productStore';

import InvisibleCardList from './InvisibleCardList';
import JoinableProductCard, { JoinableProduct } from './JoinableProductCard';
import { default as LinkCard, Link } from './LinkCard';
import ProductCard from './ProductCard';
import { FlexGrow, RowHeader, RowHeaderAction, RowHeaderWrapper, RowSubHeader } from './styled';
import TryProductCard, { TryProduct } from './TryProductCard';

export const Header = styled.div`
  font-size: 16px;
  font-weight: 600;
  height: 22px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProductContainer = styled.div`
  display: flex;
  margin: 0 -8px 16px;
  flex-wrap: wrap;
  justify-content: space-between;
`;

type InjectedProps = {
  featureFlagsStore: FeatureFlagsStore;
  productStore: ProductStore;
} & AnalyticsConsumerProps &
  JoinSiteExperimentConsumerProps;

type Props = {
  action?: JSX.Element;
  fixedCard?: JSX.Element;
  fullScreen?: boolean;
  header?: string | JSX.Element;
  hideSiteName?: boolean;
  joinable?: boolean;
  joinableProducts?: JoinableProduct[];
  links?: Link[];
  maxCardsPerRow: number;
  products?: Product[];
  singleRow?: boolean;
  subHeader?: string | JSX.Element;
  tryProducts?: TryProduct[];
} & InjectedProps;

class CardRow extends React.Component<Props> {
  render() {
    const {
      action,
      fullScreen,
      header,
      subHeader,
      joinable,
      links = [],
      products = [],
      tryProducts = [],
      joinableProducts = [],
      fixedCard,
      hideSiteName,
      maxCardsPerRow,
      singleRow,
      joinSiteExperiment,
    } = this.props;
    const productCards = products.map((product: Product) => (
      <ProductCard
        data-test-id="product-card"
        key={product.cloudId + product.productName}
        {...product}
        fullScreen={fullScreen}
        hideSiteName={hideSiteName}
        joinable={joinable}
      />
    ));
    const linkCards = links.map((link: Link) => (
      <LinkCard data-test-id="link-card" key={link.name} {...link} fullScreen={fullScreen} />
    ));

    const joinableProductCards = joinableProducts.map((product: JoinableProduct) => (
      <JoinableProductCard
        data-test-id="joinable-product-card"
        key={product.cloudId + product.productName}
        hideSiteName={hideSiteName}
        fullScreen
        {...product}
      />
    ));

    const tryProductCards = tryProducts.map((product: TryProduct) => (
      <TryProductCard data-test-id="try-product-card" key={product.name} {...product} fullScreen={fullScreen} />
    ));
    let visibleCards = [...productCards, ...joinableProductCards];
    if (joinSiteExperiment.isVariation && visibleCards.length > 0) {
      visibleCards = [...visibleCards, ...tryProductCards];
    } else {
      visibleCards = [...visibleCards, ...linkCards, ...tryProductCards];
    }

    if (singleRow) {
      visibleCards = visibleCards.slice(0, fixedCard ? maxCardsPerRow - 1 : maxCardsPerRow);
    }

    if (fixedCard) {
      visibleCards.push(fixedCard);
    }

    return (
      <React.Fragment>
        {header && (
          <RowHeaderWrapper>
            <RowHeader>{header}</RowHeader>
            {subHeader && <RowSubHeader>{subHeader}</RowSubHeader>}
            <FlexGrow />
            <RowHeaderAction>{action}</RowHeaderAction>
          </RowHeaderWrapper>
        )}
        <ProductContainer>
          {visibleCards}
          <InvisibleCardList productsCount={visibleCards.length} maxCardsPerRow={maxCardsPerRow} />
        </ProductContainer>
      </React.Fragment>
    );
  }
}

export default inject(({ featureFlagsStore, productStore }: Store) => ({
  featureFlagsStore,
  productStore,
}))(withAnalytics(withJoinSiteExperiment(CardRow)));
